import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Loader2, X, Upload } from "lucide-react";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useServiceNumbers, platformNumberFor } from "../lib/whatsapp";
import { lsGet, lsSet } from "../lib/safeStorage";

const CATEGORIES = [
  { key: "transportation",    labelEn: "Transportation Issue",       labelEs: "Problema de transporte" },
  { key: "vip_concierge",     labelEn: "VIP or Concierge Issue",     labelEs: "Problema VIP o conserje" },
  { key: "app_problem",       labelEn: "App Problem",                labelEs: "Problema de la app" },
  { key: "account",           labelEn: "Account Issue",              labelEs: "Problema de cuenta" },
  { key: "business_vendor",   labelEn: "Business or Vendor Issue",   labelEs: "Problema de negocio o vendedor" },
  { key: "other",             labelEn: "Other",                      labelEs: "Otro" },
];

const MAX_BYTES = 500_000; // ~500 KB after compression
const MAX_DIM = 1280;

/**
 * Resize + JPEG-encode a File to a compressed dataURL.
 * Falls back to the original file if the canvas pipeline fails.
 */
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Stepwise quality reduction until under MAX_BYTES.
        let q = 0.8;
        let url = canvas.toDataURL("image/jpeg", q);
        while (url.length * 0.75 > MAX_BYTES && q > 0.4) {
          q -= 0.1;
          url = canvas.toDataURL("image/jpeg", q);
        }
        resolve(url);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function getAnonId() {
  let v = lsGet("habano.anon_id");
  if (!v) {
    v = `guest-${Math.random().toString(36).slice(2, 11)}`;
    try { lsSet("habano.anon_id", v); } catch { /* ignore */ }
  }
  return v;
}

export default function SupportModal({ open, onOpenChange }) {
  const { t, lang } = useI18n();
  const platformNums = useServiceNumbers();
  const supportNum = platformNumberFor("support", platformNums);

  const [user, setUser] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("app_problem");
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { ticket_id, hasWhatsApp }
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setConfirmation(null);
    getMe().then((u) => {
      setUser(u || null);
      setName((u?.display_name || "").trim());
      setEmail((u?.email || "").trim());
    }).catch(() => {});
  }, [open]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await compressImage(f);
      setScreenshot(url);
    } catch {
      setErr(t("support_screenshot_fail"));
    }
  };

  const submit = async () => {
    setErr(null);
    if (!name.trim() || !email.trim() || !message.trim() || message.trim().length < 4) {
      setErr(t("support_missing_fields"));
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        email: email.trim(),
        category,
        message: message.trim(),
        screenshot,
        guest_id: user ? null : getAnonId(),
      };
      const { data } = await api.post("/support/ticket", body);
      const ticketId = data.ticket_id;

      // Build the WhatsApp deep link if a platform support number is configured.
      const hasWhatsApp = !!supportNum;
      if (hasWhatsApp) {
        const text = `Hi Habano Support, I have submitted a support ticket.\nTicket ID: ${ticketId}\nCategory: ${category}\nMessage: ${message.trim()}\nName: ${name.trim()}`;
        const url = `https://wa.me/${supportNum.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(text)}`;
        // Open in a new tab so the user can return and see the confirmation.
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setConfirmation({ ticket_id: ticketId, hasWhatsApp });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || t("error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="support-modal"
        className="border border-gold-strong p-0 max-w-md w-[94vw] rounded-none max-h-[90vh] overflow-hidden flex flex-col"
        style={{ background: "#171717" }}
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-gold-soft">
          <DialogTitle
            className="text-foreground"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, lineHeight: 1.2 }}
          >
            {t("support_headline")}
          </DialogTitle>
          <p className="text-foreground/65 text-[13px] mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {t("support_subtext")}
          </p>
        </DialogHeader>

        {confirmation ? (
          <div className="px-6 py-8 flex flex-col items-center text-center" data-testid="support-confirmation">
            <div
              className="mb-3"
              style={{ color: "#E8B84B", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase" }}
            >
              {t("support_ticket_label")}
            </div>
            <div
              className="font-mono mb-4"
              data-testid="support-confirm-ticket-id"
              style={{ color: "#E8B84B", fontSize: 28 }}
            >
              {confirmation.ticket_id}
            </div>
            <p className="text-foreground/85 text-[14px] leading-relaxed">
              {confirmation.hasWhatsApp ? t("support_confirm_with_wa") : t("support_confirm_no_wa")}
            </p>
            <button
              type="button"
              data-testid="support-confirm-close"
              onClick={() => onOpenChange(false)}
              className="mt-6 inline-flex items-center justify-center"
              style={{
                background: "#E8B84B", color: "#0B0B0B", border: "1px solid #E8B84B",
                borderRadius: 11, padding: "10px 22px", minHeight: 48, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif", fontSize: 14,
              }}
            >
              {t("support_done")}
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <Field label={t("support_name")}>
              <input
                data-testid="support-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-lux w-full"
                style={inputStyle}
              />
            </Field>
            <Field label={t("support_email")}>
              <input
                data-testid="support-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-lux w-full"
                style={inputStyle}
                type="email"
              />
            </Field>
            <Field label={t("support_category")}>
              <select
                data-testid="support-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input-lux w-full"
                style={inputStyle}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{lang === "es" ? c.labelEs : c.labelEn}</option>
                ))}
              </select>
            </Field>
            <Field label={t("support_message")}>
              <textarea
                data-testid="support-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder={t("support_message_placeholder")}
                className="input-lux w-full resize-y"
                style={{ ...inputStyle, minHeight: 110 }}
              />
            </Field>
            <Field label={t("support_screenshot")}>
              <label
                data-testid="support-screenshot-btn"
                className="inline-flex items-center gap-2 cursor-pointer"
                style={{
                  border: "1px solid rgba(232,184,75,0.5)", borderRadius: 11, padding: "10px 14px",
                  color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 13, minHeight: 48,
                }}
              >
                <Upload className="w-3.5 h-3.5" strokeWidth={1.6} />
                {screenshot ? t("support_screenshot_change") : t("support_screenshot_choose")}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
              </label>
              {screenshot && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={screenshot} alt="" className="w-16 h-16 object-cover rounded" data-testid="support-screenshot-preview" />
                  <button type="button" onClick={() => setScreenshot(null)} className="text-[11px] text-foreground/55 underline-none">
                    <X className="w-3 h-3 inline" /> {t("cancel")}
                  </button>
                </div>
              )}
            </Field>

            {err && (
              <div data-testid="support-error" className="text-[12px] text-amber-300 border border-amber-500/40 p-2 rounded" style={{ background: "rgba(245,158,11,0.07)" }}>
                {err}
              </div>
            )}

            <button
              type="button"
              data-testid="support-submit"
              onClick={submit}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2"
              style={{
                background: "#E8B84B", color: "#0B0B0B", border: "1px solid #E8B84B",
                borderRadius: 11, padding: "12px 18px", minHeight: 52, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif", fontSize: 14,
                opacity: busy ? 0.55 : 1, cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? t("support_submitting") : t("support_submit")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block mb-1.5"
        style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

const inputStyle = {
  background: "#1D1D1D",
  border: "1px solid rgba(232,184,75,0.25)",
  borderRadius: 10,
  padding: "12px 14px",
  color: "#F5F1E8",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 16, // ≥16px prevents iOS zoom
  minHeight: 48,
};
